package hk.com.vr360_demo2.webapp.component.editor_rewrite

import cc.easyandroid.easycompose.component.ComponentContext
import cc.easyandroid.easycompose.core.instancekeeper.getOrCreate
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaViewParameters
import hk.com.vr360_demo2.webapp.component.marzipano.PanoramaLinkHotspot
import hk.com.vr360_demo2.webapp.core.net.KtorPublicApi
import hk.com.vr360_demo2.webapp.core.net.common.PanoramaEditData
import hk.com.vr360_demo2.webapp.core.ui_state.UIComponent
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.StateFlow
import cc.easyandroid.easycompose.mvi_main.DefaultStoreFactory
import kotlinx.serialization.builtins.serializer
import org.koin.core.component.KoinComponent

interface EditorRewriteComponent {
    val id: String
    val token: String
    val state: StateFlow<EditorRewriteState>
    val errors: Flow<UIComponent>
    fun dispatch(intent: EditorRewriteIntent)
    fun save(editData: PanoramaEditData)
    fun saveCurrentHotspotsMock(
        sceneId: String,
        hotspots: List<PanoramaLinkHotspot>,
        onSuccess: () -> Unit,
    )
    fun saveCurrentInitialViewMock(
        sceneId: String,
        initialViewParameters: PanoramaViewParameters,
        onSuccess: () -> Unit,
    )
}

class DefaultEditorRewriteComponent(
    context: ComponentContext,
    override val id: String,
    override val token: String,
) : EditorRewriteComponent, KoinComponent, ComponentContext by context {

    private val publicApi get() = getKoin().get<KtorPublicApi>()
    private val restoredActiveSceneId = stateKeeper.consume(
        key = stateKey(id),
        strategy = String.serializer(),
    )

    private val viewModel: EditorRewriteViewModel =
        instanceKeeper.getOrCreate(retainedKey(id)) {
            EditorRewriteViewModel(
                id = id,
                token = token,
                publicApi = publicApi,
                restoredActiveSceneId = restoredActiveSceneId,
                storeFactory = DefaultStoreFactory(),
            )
        }
    override val state: StateFlow<EditorRewriteState> = viewModel.observeState
    override val errors: Flow<UIComponent> = viewModel.errors

    init {
        registerStateKeeper()
    }

    private fun registerStateKeeper() {
        val key = stateKey(id)
        if (!stateKeeper.isRegistered(key)) {
            stateKeeper.register(
                key = key,
                strategy = String.serializer(),
            ) {
                viewModel.state.activeSceneId
            }
        }
    }

    override fun dispatch(intent: EditorRewriteIntent) {
        viewModel.accept(intent)
    }

    override fun save(editData: PanoramaEditData) {
        viewModel.save(editData)
    }

    override fun saveCurrentHotspotsMock(
        sceneId: String,
        hotspots: List<PanoramaLinkHotspot>,
        onSuccess: () -> Unit,
    ) {
        dispatch(EditorRewriteIntent.SaveCurrentHotspotsMock(
            sceneId = sceneId,
            hotspots = hotspots,
            onSuccess = onSuccess,
        ))
    }

    override fun saveCurrentInitialViewMock(
        sceneId: String,
        initialViewParameters: PanoramaViewParameters,
        onSuccess: () -> Unit,
    ) {
        dispatch(EditorRewriteIntent.SaveCurrentInitialViewMock(
            sceneId = sceneId,
            initialViewParameters = initialViewParameters,
            onSuccess = onSuccess,
        ))
    }

    companion object {
        private const val STATE_KEY_PREFIX = "editor_rewrite_state_"
        private const val RETAINED_KEY_PREFIX = "editor_rewrite_retained_"

        private fun stateKey(id: String): String = "$STATE_KEY_PREFIX$id"
        private fun retainedKey(id: String): String = "$RETAINED_KEY_PREFIX$id"
    }
}
